import { getHomepage } from "utils";
import {
  Button,
  ConfirmationModal,
  Dropdown,
  Icon,
  TranslateWrapper,
} from "components";
import { UncontrolledTooltip } from "reactstrap";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import NiceModal from "@ebay/nice-modal-react";
import { ProjectOrFolderDownloadModal } from "components/_Modals/ProjectDownloadModal/ProjectOrFolderDownloadModal";
import { ShareProjectModal } from "components/_Modals/";
import { MultipleProjectsDeleteMut, TroxnetUploadXmlMut } from "api/mutations";
import { toast } from "react-toastify";
import * as Scrivito from "scrivito";
import { useMutation, useQuery } from "urql";
import { XMLParser } from "fast-xml-parser";
import { ProjectDataQry } from "api/queries";
import { UploadXmlModal } from "components/_Modals/UploadXmlModal";
import { useAppBaseContext } from "providers/AppBaseProvider";
import { useTranslate } from "hooks";
import { useChangeProject, useFetchProject } from "hooks/projectHooks";
import { useProjectDataStore } from "stores/projectStore";

export const ProjectHeader = memo(
  ({ title, projectId, project = null, page, productListRef = null }) => (
    <ProjectHeaderComponent
      title={title}
      projectId={projectId}
      project={project}
      page={page}
      productListRef={productListRef}
    />
  ),
);

const ProjectHeaderComponent = Scrivito.connect(
  ({ title, projectId, project = null, page, productListRef }) => {
    const translate = useTranslate();
    const refetchProject = useFetchProject();
    const setProjectId = useProjectDataStore((state) => state.setProjectId);
    const setIsManualRedirect = useProjectDataStore(
      (state) => state.setIsManualRedirect,
    );
    const isManualRedirect = useProjectDataStore(
      (state) => state.isManualRedirect,
    );
    const { lang: language } = useAppBaseContext();
    const redirectAfterDelete =
      page.get("redirectAfterDelete") || getHomepage();
    // TODO - GET_INITIAL_PROJECT_STATE remove with normal logic. Haven't found setting initial state - it's definetely not the best approach, but at least it works
    {
      const params = Scrivito.currentPageParams();
      const projectIdSaved = useProjectDataStore((state) => state.projectId);
      const handleChangeProject = useChangeProject();
      useEffect(() => {
        if (!projectIdSaved && !isManualRedirect) {
          handleChangeProject(params.projectId);
        }
      }, [
        handleChangeProject,
        params.projectId,
        projectIdSaved,
        isManualRedirect,
      ]);
    }

    const [_uploadXML, exeUploadXml] = useMutation(TroxnetUploadXmlMut);

    const handleUploadTroxNetCom = async (lang, xmlContent, setLoader) => {
      const parser = new XMLParser();
      const json = parser.parse(xmlContent);
      const variables = {
        input: { projectId, data: json, languageCode: lang },
      };

      setLoader(true);
      const { data, error } = await exeUploadXml(variables);
      const errorMessage =
        error?.message || data?.uploadTroxNetComWizard?.status !== "SUCCESS"
          ? data?.uploadTroxNetComWizard?.message
          : null;
      if (errorMessage) {
        toast.error(errorMessage);
      } else {
        if (productListRef?.current) {
          for (const key in productListRef.current) {
            if (productListRef.current[key]) {
              productListRef.current[key]();
            }
          }
        }
        refetchProject();
      }

      setLoader(false);
    };

    const [{ data }] = useQuery({
      query: ProjectDataQry,
      variables: {
        input: { id: projectId, languageCode: language },
      },
      pause: !projectId || project,
    });

    const openModal = () => {
      NiceModal.show(UploadXmlModal, {
        uploadTroxnetXml: handleUploadTroxNetCom,
      });
    };

    const projectTitle = useMemo(
      () => project?.title || data?.project?.title,
      [data?.project?.title, project?.title],
    );
    const handleDelete = useCallback(() => {
      NiceModal.show(ConfirmationModal, {
        submitBtnText: translate("PROJECT_MODAL_DELETE_TEXT"),
        title: translate("PROJECT_MODAL_DELETE_TEXT"),
        alertText: translate(
          "PROJECT_MODAL_DELETE_WARNING_TEXT_WITH_TITLE",
          projectTitle,
        ),
        mutation: MultipleProjectsDeleteMut,
        params: { input: { ids: [projectId], languageCode: language } },
      }).then(() => {
        setIsManualRedirect(true);
        const waitingBeforeRedirect = toast.loading(
          translate("IS_LOADING_TEXT"),
        );

        setTimeout(() => {
          toast.dismiss(waitingBeforeRedirect);
          Scrivito.navigateTo(redirectAfterDelete);

          // Delay reset so the old useEffect cannot run in between
          setTimeout(() => {
            setProjectId(null);
            setIsManualRedirect(false);
          }, 500);
        }, 2000);
      });
    }, [
      projectId,
      projectTitle,
      redirectAfterDelete,
      translate,
      language,
      setIsManualRedirect,
      setProjectId,
    ]);

    const openDownloadModal = useCallback(() => {
      NiceModal.show(ProjectOrFolderDownloadModal, {
        modalSubtitle: projectTitle,
        projectId,
      });
    }, [projectId, projectTitle]);

    const toggleShareModal = useCallback(() => {
      NiceModal.show(ShareProjectModal, {
        project: {
          title: project?.title || title,
          id: project?.id || projectId,
        },
      }).then();
    }, [project, projectId, title]);

    return (
      <div className="d-flex align-items-center">
        <h3 className="text-uppercase mb-0 me-3">{title}</h3>
        <div className="d-flex align-items-center">
          <Button
            data-testid="ProjectItemComponent_share-btn"
            color="transparent"
            className="px-2 py-0 lh-0 border-end shadow-none"
            id="project_item_share_icon"
            onClick={toggleShareModal}
          >
            <Icon
              prefix="trox"
              iconName="share-nodes-regular"
              className="icon-20"
            />
          </Button>
          <UncontrolledTooltip target="project_item_share_icon" placement="top">
            {translate("SHARE")}
          </UncontrolledTooltip>
          {typeof translate("SHARE") === "object" && (
            <TranslateWrapper
              tooltipText="SHARE"
              tooltipId="project_item_share_icon"
            />
          )}
          <Button
            data-testid="ProjectItemComponent_download-btn"
            color="transparent"
            className="px-2 py-0 lh-0 border-end shadow-none"
            onClick={openDownloadModal}
            id="project_item_download_icon"
          >
            <Icon
              prefix="trox"
              iconName="arrow-down-bracket-regular"
              className="icon-20"
            />
          </Button>
          <UncontrolledTooltip
            target="project_item_download_icon"
            placement="top"
          >
            {translate("DOWNLOAD")}
          </UncontrolledTooltip>
          {typeof translate("DOWNLOAD") === "object" && (
            <TranslateWrapper
              tooltipText="DOWNLOAD"
              tooltipId="project_item_download_icon"
            />
          )}

          <Dropdown
            btnChildren={
              <Icon
                prefix="trox"
                iconName="ellipsis-vertical-regular"
                className="icon-20"
                data-testid="ProjectItemComponent_more-actions-btn"
              />
            }
            btnId="project_item_more_actions_icon"
            options={[
              {
                id: "delete",
                title: translate("DELETE"),
                onClick: handleDelete,
              },
              {
                id: "uploadTroxNetCom",
                title: "Upload TROXNETCOM",
                onClick: openModal,
              },
            ]}
          />
          <UncontrolledTooltip
            target="project_item_more_actions_icon"
            placement="top"
          >
            {translate("MORE_ACTIONS")}
          </UncontrolledTooltip>
          {typeof translate("MORE_ACTIONS") === "object" && (
            <TranslateWrapper
              tooltipText="MORE_ACTIONS"
              tooltipId="project_item_more_actions_icon"
              placement="top"
            />
          )}
        </div>
      </div>
    );
  },
);
